import { computed, watch } from 'vue';
import { QueryKey } from '@/api/common/types/query-key.enum';
import { useQuery } from '@tanstack/vue-query';
import { getUser } from '../user.api';
import { useAuthState } from '@/state/user';
import { firebaseAuth } from '@/api/auth/auth.api';

export const useUser = () => {
  const { userAuth, setBackendUserProvisioned } = useAuthState();

  // Computed fallback: check both userAuth.value and firebaseAuth.currentUser
  // This ensures query enables immediately after login, even if userAuth.value
  // hasn't been updated yet due to async onAuthStateChanged on mobile
  const hasAuth = computed(() => {
    return !!userAuth.value?.uid || !!firebaseAuth.currentUser?.uid;
  });

  const query = useQuery({
    queryKey: [QueryKey.GetLoggedInUser],
    queryFn: getUser,
    enabled: hasAuth,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // `/api/user` is the one endpoint that provisions a missing GYMing document,
  // which makes a successful response the authoritative readiness signal for
  // everything else. It already runs in parallel with startup, so gating on it
  // costs a healthy returning user no extra round trip.
  watch(
    query.data,
    user => {
      if (user?.id) setBackendUserProvisioned(true);
    },
    { immediate: true }
  );

  // User is ready when all required nested properties exist
  const isUserReady = computed(() => {
    const user = query.data.value;
    return (
      !!user &&
      !!user.id &&
      !!user.profile &&
      typeof user.profile.fullName === 'string' &&
      user.profile.fullName.length > 0 &&
      Array.isArray(user.following) &&
      Array.isArray(user.savedPosts)
    );
  });

  return {
    ...query,
    isUserReady,
  };
};
